/**
 * TypingIndicator — three amber dots pulsing while the agent thinks.
 *
 * Visual contract (locked 2026-06-15):
 *   - Same avatar + indent pattern as AgentMessage so the dot stack
 *     appears "in line" with where the next agent message will land
 *   - Three 6px amber dots, 4px gap, pulse animation 1.4s ease-in-out
 *   - Staggered 0 / 0.2s / 0.4s for the wave effect
 *   - Respects `prefers-reduced-motion`: the pulse flattens to a
 *     steady opacity AND the framer-motion entrance is gated
 *
 * Rendered only when AgentClient.isLoading === true and the latest
 * thread message is a user message awaiting reply.
 */

"use client";

import { motion, useReducedMotion } from "framer-motion";

import { MOTION, ms } from "@/lib/design-tokens";

export function TypingIndicator() {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{
        duration: ms(reduce ? MOTION.fast : MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
      }}
      className="flex max-w-[92%] gap-3"
      role="status"
      aria-label="Octopusden is thinking"
    >
      <div
        className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full"
        style={{
          background: "rgba(244,169,60,0.15)",
          color: "#f4a93c",
        }}
        aria-hidden="true"
      >
        <span style={{ fontSize: "var(--fs-small)", lineHeight: 1 }}>✦</span>
      </div>
      <div className="agent-typing flex items-center" style={{ padding: "14px 16px" }}>
        <span />
        <span />
        <span />
      </div>

      <style jsx>{`
        .agent-typing :global(span) {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin-right: 4px;
          border-radius: 50%;
          background: rgba(244, 169, 60, 0.7);
          animation: agent-typing-pulse 1.4s ease-in-out infinite;
        }
        .agent-typing :global(span:nth-child(2)) {
          animation-delay: 0.2s;
        }
        .agent-typing :global(span:nth-child(3)) {
          animation-delay: 0.4s;
        }
        @keyframes agent-typing-pulse {
          0%,
          60%,
          100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          30% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .agent-typing :global(span) {
            animation: none;
            opacity: 0.7;
          }
        }
      `}</style>
    </motion.div>
  );
}
